import { SimpleGrid, Stack, useBreakpointValue } from "@chakra-ui/react";
import type { ReactElement } from "react";
import type { HomeConfig, PanelKind } from "./homeConfig";
import { GreetingHeader } from "./GreetingHeader";
import { HeroActions } from "./HeroActions";
import { HomeSearchBar } from "./HomeSearchBar";
import { splitIntoColumns } from "./panelColumns";
import { ActiveContractsPanel } from "./panels/ActiveContractsPanel";
import { MyBidsPanel } from "./panels/MyBidsPanel";
import { MyNeedsPanel } from "./panels/MyNeedsPanel";
import { OpenProcurementsPanel } from "./panels/OpenProcurementsPanel";
import { ProcurementPhasesPanel } from "./panels/ProcurementPhasesPanel";
import { RecentContractsPanel } from "./panels/RecentContractsPanel";
import { SourcingEventsPanel } from "./panels/SourcingEventsPanel";
import { ContractExpiryPanel } from "./panels/ContractExpiryPanel";
import { UpcomingDeadlinesPanel } from "./panels/UpcomingDeadlinesPanel";

const PANEL: Record<PanelKind, () => ReactElement> = {
  sourcingEvents: SourcingEventsPanel,
  myNeeds: MyNeedsPanel,
  activeContracts: ActiveContractsPanel,
  myBids: MyBidsPanel,
  openProcurements: OpenProcurementsPanel,
  upcomingDeadlines: UpcomingDeadlinesPanel,
  contractExpiry: ContractExpiryPanel,
  procurementPhases: ProcurementPhasesPanel,
  recentContracts: RecentContractsPanel,
};

/**
 * Each column stacks on its own rather than sharing grid rows, so a short panel next to a tall one
 * no longer leaves the height difference as empty space below it.
 */
export const HomeShell = ({ config }: { config: HomeConfig }) => {
  const columnCount = useBreakpointValue({ base: 1, lg: 2 }, { ssr: false }) ?? 2;
  const columns = splitIntoColumns(config.panels, columnCount);

  return (
    <>
      <GreetingHeader headlineKey={config.headlineKey} />
      <HomeSearchBar />
      <HeroActions actions={config.heroActions} />
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="4" alignItems="start">
        {columns.map((panels, index) => (
          <Stack key={index} spacing="4">
            {panels.map((kind) => {
              const Panel = PANEL[kind];
              return <Panel key={kind} />;
            })}
          </Stack>
        ))}
      </SimpleGrid>
    </>
  );
};
